import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Copy, CheckCircle2, Heart, DollarSign, Building2, PawPrint } from 'lucide-react';
import BankTransferDetails from '../components/common/BankTransferDetails';
import { PayUPayment } from '../components/payments';

export function DonationPage() {
  const [copiedField, setCopiedField] = React.useState<string | null>(null);

  const copyToClipboard = (text: string, field: string) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 2000);
    });
  };

  return (
    <div className="max-w-7xl mx-auto px-4 py-16">
      <Helmet>
        <title>Wpłata jednorazowa | Fundacja "Znajdki"</title>
        <meta name="description" content="Wesprzyj Fundację Znajdki jednorazową wpłatą. Dostępne metody: BLIK, PayPal, przelew bankowy." />
      </Helmet>

      {/* Hero Section */}
      <div className="text-center mb-16">
          <h1 className="heading-1 mb-6">
            Wpłata jednorazowa
          </h1>
        <div className="relative w-full max-w-4xl mx-auto mb-12 rounded-2xl overflow-hidden shadow-2xl">
          <img
            src="/images/animals/fundacja-znajdki-img-6134.webp"
            alt="Kotki z Fundacji Znajdki"
            className="w-full h-64 sm:h-72 md:h-80 lg:h-[500px] xl:h-[600px] object-cover object-center object-cover object-[0%_10%]"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
          <div className="absolute bottom-0 left-0 right-0 p-8 text-white">
              <p className="heading-4-white font-medium max-w-2xl mx-auto no-margin">
                Każda złotówka ma znaczenie dla naszych podopiecznych
              </p>
          </div>
        </div>
          <p className="body-large max-w-3xl mx-auto">
            Wybierz najwygodniejszą dla Ciebie metodę wpłaty i wesprzyj bezdomne zwierzęta.
          </p>
      </div>

      {/* Quick Payment Methods */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16">
        {/* BLIK */}
        <div className="bg-white dark:bg-accessible-gray-800 high-contrast:bg-black p-8 rounded-lg shadow-lg">
          <div className="mb-4 flex items-center">
            <img
              src="/images/payments/BLIK/blik.svg"
              alt="Logo BLIK"
              className="h-8 md:h-10 w-auto" 
              loading="lazy"
            />
          </div>
          <div className="mb-6">
            <img
              src="/photos/IMG_1162.webp"
              alt="Szczeniak z fundacji"
              loading="lazy"
              className="w-full h-44 sm:h-56 md:h-64 object-cover object-center rounded-lg"
            />
          </div>
          
          <div className="bg-accessible-gray-50 dark:bg-accessible-gray-700 high-contrast:bg-black p-6 rounded-lg mb-6">
            <div className="flex items-center justify-between">
              <div>
                  <p className="label-medium mb-1">
                    Numer telefonu
                  </p>
                <p className="heading-3 font-mono">
                  664-821-160
                </p>
              </div>
              <button
                onClick={() => copyToClipboard('664821160', 'blik')}
                className="p-2 hover:bg-accessible-gray-200 dark:hover:bg-accessible-gray-600 rounded-lg transition-colors"
                title="Skopiuj numer BLIK"
              >
                {copiedField === 'blik' ? (
                  <CheckCircle2 className="w-5 h-5 text-green-600" />
                ) : (
                  <Copy className="w-5 h-5 text-accessible-gray-700 dark:text-white/90 high-contrast:text-white" />
                )}
              </button>
            </div>
          </div>
          
          <div className="flex items-center gap-4 mb-2">
              <h2 className="heading-3">
                BLIK
              </h2>
          </div>
            <p className="body-medium">
              Najszybszy sposób wpłaty. Wpisz numer telefonu w aplikacji bankowej i potwierdź przelew.
            </p>
        </div>

        {/* PayPal */}
        <div className="bg-white dark:bg-accessible-gray-800 high-contrast:bg-black p-8 rounded-lg shadow-lg">
          
          
          <div className="mb-4 flex items-center">
            <img
              src="/images/payments/paypal/paypal.svg"
              alt="Logo PayPal"
              className="h-8 md:h-10 w-auto"
              loading="lazy"
            />
          </div>
          <div className="mb-6">
            <img
              src="/photos/rudy-kociak-fundacja-znajdki.webp"
              alt="Rudy kociak z fundacji"
              loading="lazy"
              className="w-full h-44 sm:h-56 md:h-64 object-cover object-center rounded-lg"
            />
          </div>
          
          <div className="bg-accessible-gray-50 dark:bg-accessible-gray-700 high-contrast:bg-black p-6 rounded-lg mb-6">
            <div className="flex items-center justify-between">
              <div>
                  <p className="label-medium mb-1">
                    Adres email
                  </p>
                <p className="heading-5 font-mono">
                  przytulisko.jb@o2.pl
                </p>
              </div>
              <button
                onClick={() => copyToClipboard('przytulisko.jb@o2.pl', 'paypal')}
                className="p-2 hover:bg-accessible-gray-200 dark:hover:bg-accessible-gray-600 rounded-lg transition-colors"
                title="Skopiuj adres PayPal"
              >
                {copiedField === 'paypal' ? (
                  <CheckCircle2 className="w-5 h-5 text-green-600" />
                ) : (
                  <Copy className="w-5 h-5 text-accessible-gray-700 dark:text-white/90 high-contrast:text-white" />
                )}
              </button>
            </div>
          </div>
          <div className="flex items-center gap-4 mb-2">
              <h2 className="heading-3">
                PayPal
              </h2>
          </div>
            <p className="body-medium">
              Bezpieczne płatności online z całego świata. Idealne dla wpłat zagranicznych.
            </p>
        </div>
      </div>

      {/* PayU Payment Section */}
      <div className="mb-16">
        <div className="max-w-2xl mx-auto">
          <PayUPayment />
        </div>
      </div>

      {/* Gallery Section */}
      <div className="mb-16">
          <h2 className="heading-2 text-center mb-8">
            Twoje wsparcie pomaga takim zwierzakom
          </h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="relative rounded-lg overflow-hidden aspect-[4/3] md:aspect-[3/2]">
            <img
              src="/photos/szczeniaki-w-kartonie-fundacja-znajdki.webp"
              alt="Szczeniaki w kartonie"
              loading="lazy"
              className="w-full h-full object-cover object-center hover:scale-105 transition-transform duration-300"
            />
          </div>
          <div className="relative rounded-lg overflow-hidden aspect-[4/3] md:aspect-[3/2]">
            <img
              src="/photos/szary-kotek-znajdki-fundacja.webp"
              alt="Szary kotek z fundacji"
              loading="lazy"
              className="w-full h-full object-cover object-center hover:scale-105 transition-transform duration-300"
            />
          </div>
          <div className="relative rounded-lg overflow-hidden aspect-[4/3] md:aspect-[3/2]">
            <img
              src="/photos/piesek-znajdki-kleszcze.webp"
              alt="Piesek z fundacji"
              loading="lazy"
              className="w-full h-full object-cover object-center hover:scale-105 transition-transform duration-300"
            />
          </div>
          <div className="relative rounded-lg overflow-hidden aspect-[4/3] md:aspect-[3/2]">
            <img
              src="/photos/zmeczony-kotek-w-kolnierzu.webp"
              alt="Zmęczony kotek w kołnierzu"
              loading="lazy"
              className="w-full h-full object-cover object-center hover:scale-105 transition-transform duration-300"
            />
          </div>
        </div>
      </div>

      {/* Bank Transfer Details */}
      <BankTransferDetails className="mb-16" />

      {/* Impact Section */}
      <div className="bg-accessible-gray-50 dark:bg-accessible-gray-700 high-contrast:bg-black p-8 rounded-lg mb-16">
        <div className="text-center mb-8">
            <h2 className="heading-2 mb-4">
              Na co przeznaczamy Twoje wsparcie?
            </h2>
          <p className="body-large">
            Każda złotówka trafia bezpośrednio na pomoc zwierzętom
          </p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          <div className="text-center">
            <div className="w-16 h-16 bg-accessible-red-600/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <PawPrint className="w-8 h-8 text-accessible-red-600" />
            </div>
              <h3 className="heading-4 mb-2">
                Opieka weterynaryjna
              </h3>
            <p className="body-medium">
              Leczenie, szczepienia, sterylizacje i kastracje naszych podopiecznych
            </p>
          </div>
          
          <div className="text-center">
            <div className="w-16 h-16 bg-accessible-red-600/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <Heart className="w-8 h-8 text-accessible-red-600" />
            </div>
              <h3 className="heading-4 mb-2">
                Wyżywienie
              </h3>
            <p className="body-medium">
              Wysokiej jakości karma i specjalistyczne diety dla chorych zwierząt
            </p>
          </div>
          
          <div className="text-center">
            <div className="w-16 h-16 bg-accessible-red-600/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <Building2 className="w-8 h-8 text-accessible-red-600" />
            </div>
              <h3 className="heading-4 mb-2">
                Utrzymanie schroniska
              </h3>
            <p className="body-medium">
              Ogrzewanie, środki czystości, naprawy i modernizacje pomieszczeń
            </p>
          </div>
        </div>
      </div>

      {/* Tax Deduction Info */}
      <div className="bg-accessible-red-600 rounded-lg p-8 text-white mb-16">
        <div className="flex items-center gap-4 mb-6">
          <DollarSign className="w-12 h-12" />
          <div>
              <h2 className="heading-3-white mb-2">
                Przekaż nam 1.5% podatku
              </h2>
            <p className="body-medium-white-90">
              To nic Cię nie kosztuje, a dla nas znaczy bardzo wiele
            </p>
          </div>
        </div>
        
        <div className="bg-white/10 p-6 rounded-lg">
          <div className="flex items-center justify-between">
            <div>
                <p className="label-medium-white text-white/90 mb-1">
                  Numer KRS
                </p>
              <p className="heading-3-white font-mono no-margin">
                0000437387
              </p>
            </div>
            <button
              onClick={() => copyToClipboard('0000437387', 'krs')}
              className="p-2 hover:bg-white/10 rounded-lg transition-colors"
              title="Skopiuj numer KRS"
            >
              {copiedField === 'krs' ? (
                <CheckCircle2 className="w-5 h-5 text-green-400" />
              ) : (
                <Copy className="w-5 h-5 text-white" />
              )}
            </button>
          </div>
        </div>
        
        <div className="mt-6">
          <a 
            href="/przekaz-podatek"
            className="inline-flex items-center gap-2 bg-white text-accessible-red-600 px-6 py-3 rounded-lg hover:bg-accessible-gray-50 transition-colors font-semibold"
          >
            <span>Dowiedz się więcej</span>
          </a>
        </div>
      </div>

      {/* Contact Info */}
      <div className="text-center bg-accessible-gray-50 dark:bg-accessible-gray-700 high-contrast:bg-black p-8 rounded-lg">
          <h2 className="heading-3 mb-4">
            Potrzebujesz pomocy?
          </h2>
        <p className="body-medium mb-6">
          Jeśli masz pytania dotyczące wpłat, skontaktuj się z nami
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <a 
            href="tel:+48664821160"
            className="inline-flex items-center gap-2 bg-accessible-red-600 text-white px-6 py-3 rounded-lg hover:bg-accessible-red-700 transition-colors"
          >
            <span>664 821 160</span>
          </a>
          <a 
            href="mailto:znajdki2012@gmail.com"
            className="inline-flex items-center gap-2 bg-white dark:bg-accessible-gray-800 text-accessible-gray-900 dark:text-white px-6 py-3 rounded-lg hover:bg-accessible-gray-100 dark:hover:bg-accessible-gray-600 transition-colors border border-accessible-gray-200 dark:border-accessible-gray-600"
          >
            <span>znajdki2012@gmail.com</span>
          </a>
        </div>
      </div>
    </div>
  );
}